<template>
    <ChartPortlet
        ref="chartPortlet"
        :title="i18n('dashboard.cancellationsChart.chartTitle')"
        :chartHeight="fullChartHeight"
        :useDescription="false"
    />
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { DashboardDao } from "@/data/dao/service/DashboardDao";
    import type { ICancellationUserInfo } from "@/data/model/Dashboard";
    import { CancellationReason, DashboardStatsPeriod } from "@/data/model/Enums";
    import { i18n } from "@/portal/Trans";
    import { logError } from "@/shared/logging/errors";
    import { Chart } from "chart.js/auto";
    import { computed, nextTick, onMounted, ref, watch } from "vue";

    import ChartPortlet from "@/portal/components/dashboard/ChartPortlet.vue";

    import { CHART_COLORS } from "./Utils";
    import {
        barStyle,
        barStyleDefaultColors,
        categoriesScaleRef,
        defaultTooltipSettings,
        displayPointMoneyValue,
        visibleLegend,
    } from "./sharedChartUtils";
    import { defaultBarChartLayout, fullChartHeight, moneyScale } from "./sharedChartUtils";

    type ReasonMapping = {
        reason: CancellationReason | null;
        label: string;
    };

    const dao = Factory.getInstance<DashboardDao>(DashboardDao);

    const props = defineProps<{
        locationId?: string;
        period?: DashboardStatsPeriod;
        selectedDate?: Date;
    }>();

    let chart: Chart;
    const labels = ref<string[]>([]);
    let reasons: ReasonMapping[] = [];
    let users: ICancellationUserInfo[] = [];

    const isInitialized = ref(false);
    const chartPortlet = ref<InstanceType<typeof ChartPortlet> | null>(null);
    const canvas = computed(() => chartPortlet.value?.canvasElement);
    const paramsLoaded = computed(() => props.locationId !== undefined && props.period !== undefined);

    // create chart if all params are provided and is first init or init after not all params were provided
    onMounted(initializeChart);
    watch(paramsLoaded, initializeChart);

    // reload data if any prop is changed
    watch(() => props.locationId, refreshData);
    watch(() => props.period, refreshData);
    watch(() => props.selectedDate, refreshData);

    async function initializeChart() {
        if (!paramsLoaded.value) return;

        await nextTick();
        createChart();
        loadData();
    }

    function createChart() {
        const ctx = canvas.value!.getContext("2d")!;

        chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: [],
                datasets: [
                    {
                        label: i18n("dashboard.cancellationsChart.chartTitle"),
                        ...barStyle(CHART_COLORS.darkPink, CHART_COLORS.lightPink),
                        data: [],
                    },
                ],
            },
            options: {
                skipNull: true,
                scales: {
                    x: {
                        ...categoriesScaleRef(labels),
                        stacked: true,
                    },
                    y: {
                        ...moneyScale(),
                        stacked: true,
                    },
                },
                ...defaultBarChartLayout,
                plugins: {
                    legend: visibleLegend,
                    tooltip: {
                        ...defaultTooltipSettings,
                        callbacks: {
                            title: (context) => {
                                const xAxisIndex = context[0].parsed.x;
                                const reasonLabel = labels.value[xAxisIndex];
                                const userLabel = context[0].dataset.label;
                                return `${reasonLabel}\n${userLabel}`;
                            },
                            label: (context) => displayPointMoneyValue(context),
                        },
                    },
                },
            },
        });

        isInitialized.value = true;
    }

    async function refreshData() {
        if (!isInitialized.value) return;

        loadData();
    }

    async function loadData() {
        try {
            const summary = await dao.getCancellationsSummary(props.locationId!, props.period!, props.selectedDate);
            reasons = summary.cancellationReasonSummaries.map((x) => {
                return {
                    reason: x.reason ?? null,
                    label:
                        x.reason != undefined
                            ? i18n(`enums.CancellationReason.${CancellationReason[x.reason]}`)
                            : i18n("dashboard.cancellationsChart.noReason"),
                };
            });

            users = summary.cancellationUsers;
            labels.value = reasons.map((x) => x.label);

            chart.data.labels = labels.value;
            const datasets: any[] = [];

            for (const user of users) {
                const amountsByReason: Array<number | null> = [];
                for (const reason of reasons) {
                    const reasonToFind = reason.reason == null ? undefined : reason.reason;
                    const reasonSummary = summary.cancellationReasonSummaries.find((x) => x.reason == reasonToFind);
                    if (reasonSummary) {
                        const reasonSummaryForIdentity = reasonSummary.summariesByIdentity.find(
                            (x) => x.identityId == user.identityId
                        );
                        const amountForReasonAndIdentity = reasonSummaryForIdentity?.amount ?? null;
                        amountsByReason.push(amountForReasonAndIdentity);
                    }
                }
                const userDataSet = {
                    label: user.userName,
                    ...barStyleDefaultColors(),
                    data: amountsByReason,
                };
                datasets.push(userDataSet);
            }
            chart.data.datasets = datasets;

            chart.update();
        } catch (e) {
            logError(e);
        }
    }
</script>
